import { Injectable } from '@angular/core';
import { WhitelabelService } from '@galaxy/partner';
import { Observable } from 'rxjs';
import { map, share, shareReplay, switchMap } from 'rxjs/operators';
import { partnerId } from '../../globals';
import { LocationsService } from '../locations';
import { IPartnerMarketConfigService } from './interface';
import { ConfigurationInterface, TimeRange } from '@vendasta/partner';
import { BusinessAppConfig } from './features';

// This Feature enum contains all of the features that partner center client needs to know about
export const enum Feature {
  contentLibrary = 'content-library',
  guides = 'guides',
  localMarketingIndex = 'local-marketing-index',
  recommendations = 'recommendations',
  executiveReport = 'vbc-executive-report',
  meetingScheduler = 'meeting_scheduler_business_app',
  marketplace = 'access-marketplace',
  sscAccess = 'ssc-access',
  brandsAccess = 'brands',
  whitelabel = 'white-labeling',
  multiLocationReport = 'multi-location-report',
  crmAISummary = 'crm-ai-summary',
  automations = 'automations',
}

@Injectable({ providedIn: 'root' })
export class PartnerMarketConfigService implements IPartnerMarketConfigService {
  private readonly config$: Observable<ConfigurationInterface> = this.locationService.marketId$.pipe(
    switchMap((marketId) => this.whitelabelService.getConfiguration(partnerId, marketId)),
    shareReplay(1),
  );

  // partnerFeatures are features that a partner has access to via their subscription
  private readonly partnerFeatures$: Observable<string[]>;
  // businessAppConfig holds the features that a partner has turned on and off for a market
  private readonly _businessAppConfig$: Observable<BusinessAppConfig>;

  constructor(
    private whitelabelService: WhitelabelService,
    private locationService: LocationsService,
  ) {
    this._businessAppConfig$ = this.config$.pipe(
      map(
        (config) =>
          ({
            ...config.businessCenterConfiguration,
            singleLocationDefaultTimeRange: this.toTimeRangeKey(
              config.businessCenterConfiguration.singleLocationDefaultTimeRange,
            ),
            multiLocationDefaultTimeRange: this.toTimeRangeKey(
              config.businessCenterConfiguration.multiLocationDefaultTimeRange,
            ),
            showStore: config.hasProductMarketplace && config.businessCenterConfiguration.showStore,
          }) as BusinessAppConfig,
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.partnerFeatures$ = this.config$.pipe(
      map((config) => config.enabledFeatures),
      map((enabled) => {
        if (enabled.indexOf(Feature.contentLibrary) > -1) {
          // Until we get actual "Guides" feature back from PC - Graham
          enabled.push(Feature.guides);
        }
        if (enabled.indexOf(Feature.localMarketingIndex) > -1) {
          // Until we get actual "Recommendations" feature back from PC - Graham
          enabled.push(Feature.recommendations);
        }
        return enabled;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getFeatures(): Observable<string[]> {
    return this.partnerFeatures$;
  }

  hasAccessToFeature(feature: Feature): Observable<boolean> {
    return this.partnerFeatures$.pipe(map((features) => Boolean(features && features.includes(feature)))).pipe(share());
  }

  get businessAppConfig$(): Observable<BusinessAppConfig> {
    return this._businessAppConfig$;
  }

  private toTimeRangeKey(timeRange: TimeRange) {
    switch (timeRange) {
      case TimeRange.TIME_RANGE_SEVEN_DAYS:
        return '7days';
      case TimeRange.TIME_RANGE_THIRTY_DAYS:
        return '30days';
      case TimeRange.TIME_RANGE_NINETY_DAYS:
        return '90days';
      case TimeRange.TIME_RANGE_SIX_MONTHS:
        return '6months';
      case TimeRange.TIME_RANGE_TWELVE_MONTHS:
        return '12months';
      default:
        return '7days';
    }
  }
}
